<template>
  <div class="flex flex-col gap-4">
    <p class="m-0 text-surface-600 dark:text-surface-300 max-w-prose">
      {{ upcomingOnly ? t('mail_service_recipients_upcoming', { event: eventTitle }) : t('mail_service_recipients_all', { event: eventTitle }) }}
    </p>

    <!-- One line: tickets · addresses · orders without an address (each part only when it is not zero) -->
    <p class="m-0 tabular-nums flex flex-wrap items-center gap-x-2" aria-live="polite">
      <ProgressSpinner v-if="counting && !count" class="!size-4" strokeWidth="6" />
      <template v-else-if="count">
        <template v-if="!count.total && !count.no_email">{{ t('mail_service_count_nobody') }}</template>
        <template v-else>
          <span v-if="count.tickets">{{ t('mail_service_count_tickets', { n: num(count.tickets) }, count.tickets) }}</span>
          <span v-if="count.tickets && count.reachable" aria-hidden="true">·</span>
          <strong v-if="count.reachable" class="text-surface-900 dark:text-surface-0">
            {{ t('mail_service_count_addresses', { n: num(count.reachable) }, count.reachable) }}
          </strong>
          <template v-if="count.total > count.reachable">
            <span aria-hidden="true">·</span>
            <span class="text-surface-500">{{ t('mail_service_count_bounced', { n: num(count.total - count.reachable) }, count.total - count.reachable) }}</span>
          </template>
          <template v-if="count.no_email">
            <span aria-hidden="true">·</span>
            <button type="button" class="underline underline-offset-2 text-surface-600 dark:text-surface-300"
              :aria-expanded="noEmailOpen" @click="toggleNoEmail">
              {{ t('mail_service_count_no_email', { n: num(count.no_email) }, count.no_email) }}
            </button>
          </template>
        </template>
      </template>
    </p>

    <ul v-if="noEmailOpen && noEmailOrders.length" class="m-0 p-0 list-none flex flex-col gap-1 text-sm">
      <li v-for="o in noEmailOrders" :key="o.id">
        <NuxtLink :to="`/events/${eventId}/orders/${o.id}`" class="underline underline-offset-2">
          {{ o.buyer_name || t('mail_service_order_no_name') }}
        </NuxtLink>
        <span class="text-surface-500 tabular-nums"> · {{ date(o.created_at) }}</span>
      </li>
      <li v-if="noEmailMore" class="text-surface-500">{{ t('mail_service_no_email_more', { n: num(noEmailMore) }) }}</li>
    </ul>

    <Button v-if="!showSlots && canPickSlots && !disabled" type="button" text size="small" icon="pi pi-plus"
      class="self-start -ml-3" :label="t('mail_service_pick_slots')" @click="slotsOpen = true" />
    <div v-if="showSlots" class="flex flex-col gap-3 max-w-xl">
      <label :for="`${uid}-slots`" class="font-medium">{{ t('mail_service_slots') }}</label>
      <MultiSelect :inputId="`${uid}-slots`" v-model="timeslotIds" :options="slotOptions" optionLabel="label" optionValue="id"
        display="chip" :maxSelectedLabels="3" filter :filterPlaceholder="t('search')"
        :placeholder="upcomingOnly ? t('mail_service_slots_all_upcoming') : t('mail_service_slots_all')"
        :disabled="disabled" class="w-full" />
      <div v-if="pastCount" class="flex items-center gap-2">
        <Checkbox :inputId="`${uid}-past`" v-model="includePast" binary :disabled="disabled" />
        <label :for="`${uid}-past`" class="text-sm">{{ t('mail_service_slots_include_past') }}</label>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { Database } from '~~/types/supabase';
import type { Segment, SegmentCount } from '~/components/Mail/Segment/SegmentBuilder.vue';
import formatTimeslot from '~/utils/formatTimeslot';

// Who gets a message about their visit: the ticket holders of one activity (mail_service_recipients).
const props = defineProps<{ modelValue: Segment; disabled?: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [Segment]; count: [SegmentCount | null] }>();

const { t } = useI18n();
const supabase = useSupabaseClient<Database>();
const { organizationId } = useMail();
const { num, date } = useLocaleFormat();
const uid = useId();

const eventId = computed(() => props.modelValue.event_ids?.[0] ?? '');
const upcomingOnly = computed(() => props.modelValue.upcoming_only !== false);
const timeslotIds = computed({
  get: () => props.modelValue.timeslot_ids ?? [],
  set: (value: string[]) => emit('update:modelValue', { ...props.modelValue, timeslot_ids: value }),
});
const includePast = computed({
  get: () => !upcomingOnly.value,
  set: (on: boolean) => emit('update:modelValue', {
    ...props.modelValue,
    upcoming_only: !on,
    // past slots you picked go when you switch them off again
    timeslot_ids: on ? timeslotIds.value : timeslotIds.value.filter(id => slots.value.find(s => s.id === id)?.upcoming),
  }),
});

// The activity and its timeslots
type Slot = { id: string; label: string; upcoming: boolean };
const eventTitle = ref('');
const slots = ref<Slot[]>([]);
watch(eventId, async (id) => {
  if (!id) return;
  const [ev, ts] = await Promise.all([
    supabase.from('events').select('title').eq('id', id).single(),
    supabase.from('event_timeslots').select('*').eq('event_id', id).order('start_date'),
  ]);
  eventTitle.value = ev.data?.title ?? '';
  const now = Date.now();
  slots.value = (ts.data ?? []).map(s => ({
    id: s.id, label: formatTimeslot(s), upcoming: new Date(s.end_date ?? s.start_date).getTime() >= now,
  }));
}, { immediate: true });

const slotOptions = computed(() => slots.value.filter(s => !upcomingOnly.value || s.upcoming));
const pastCount = computed(() => slots.value.filter(s => !s.upcoming).length);
// Only worth offering when there is something to choose between
const canPickSlots = computed(() => slotOptions.value.length > 1 || pastCount.value > 0);
const slotsOpen = ref(false);
const showSlots = computed(() => slotsOpen.value || !!timeslotIds.value.length || !upcomingOnly.value);

// Orders without a usable address, so you can reach them another way
const NO_EMAIL_SHOWN = 20;
const noEmailOpen = ref(false);
const noEmailOrders = ref<{ id: string; buyer_name: string | null; created_at: string }[]>([]);
const noEmailMore = ref(0);
const toggleNoEmail = async () => {
  noEmailOpen.value = !noEmailOpen.value;
  if (!noEmailOpen.value || !organizationId.value) return;
  const { data: ids } = await supabase.rpc('mail_segment_no_email', { p_org: organizationId.value, p_segment: props.modelValue as never });
  const all = (ids ?? []).map(r => r.order_id);
  noEmailMore.value = Math.max(0, all.length - NO_EMAIL_SHOWN);
  const { data } = all.length
    ? await supabase.from('orders').select('id, buyer_name, created_at').in('id', all.slice(0, NO_EMAIL_SHOWN))
    : { data: [] };
  noEmailOrders.value = data ?? [];
};

// Live count
const count = ref<SegmentCount | null>(null);
const counting = ref(false);
const recount = async () => {
  if (!organizationId.value || !eventId.value) return;
  counting.value = true;
  const { data, error } = await supabase.rpc('mail_segment_count', {
    p_org: organizationId.value, p_segment: props.modelValue as never, p_kind: 'service',
  });
  counting.value = false;
  count.value = error ? null : ((data as SegmentCount[] | null)?.[0] ?? { total: 0, reachable: 0, tickets: 0, no_email: 0 });
  noEmailOpen.value = false;
  emit('count', count.value);
};
watchDebounced(() => [organizationId.value, props.modelValue], recount, { debounce: 400, deep: true, immediate: true });
</script>
